(function() {
  'use strict';
  
  if (window.ProductSpecsTable) {
    return;
  }

  class ProductSpecsTable {
    constructor(container, productHandle) {
      this.container = container;
      this.productHandle = productHandle;
      this.currentVariantId = null;
      this.sectionId = container.dataset.sectionId;
      this.config = {
        namespace: container.dataset.namespace || 'specs',
        tableName: container.dataset.tableName || 'Specifications',
        showSku: container.dataset.showSku === 'true',
        showWeight: container.dataset.showWeight === 'true'
      };
      this.workerUrl = 'https://shopify-specs-api-v2.chris-comben.workers.dev/';
      this.showAllVariants = false;
      this.useImperial = false;
    }

    // Add storage helpers
    getStoredState(key) {
      try {
        return localStorage.getItem(key) === 'true';
      } catch {
        return false;
      }
    }

    setStoredState(key, value) {
      try {
        localStorage.setItem(key, value ? 'true' : 'false');
      } catch {}
    }

    async init() {
      try {
        //console.log('Product handle:', this.productHandle);
        //console.log('Config:', this.config);

        const data = await this.fetchProductData();
        const mappings = await this.fetchSpecsMappings();
        
        //console.log('Product data:', data);
        //console.log('Mappings:', mappings);
        
        // Render the table and wait for it to complete
        await this.render(data, mappings);
        
        // NOW setup toggles after table is fully rendered
        this.setupConversionToggle();
        this.setupShowAllToggle();
        this.attachEventListeners();

      } catch (error) {
        console.error('Error loading specs table:', error); //keep
        this.renderError();
      }
    }

    async fetchProductData() {
      //console.log('Fetching product via Cloudflare Worker...');

      const response = await fetch(this.workerUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          productHandle: this.productHandle
        })
      });

      const result = await response.json();
      //console.log('Worker response:', result);

      if (result.errors) {
        throw new Error('GraphQL Error: ' + JSON.stringify(result.errors)); //Keep
      }

      if (!result.data || !result.data.product) {
        throw new Error('No product found');
      }

      return result.data.product;
    }

    async fetchSpecsMappings() {
      const cacheKey = 'shopify-specs-mappings';
      const cacheTimestampKey = 'shopify-specs-mappings-timestamp';
      const cacheExpiry = 30 * 60 * 1000; // 30 minutes in milliseconds

      // Try to get from cache
      try {
        const cachedData = sessionStorage.getItem(cacheKey);
        const cachedTimestamp = sessionStorage.getItem(cacheTimestampKey);
        
        if (cachedData && cachedTimestamp) {
          const age = Date.now() - parseInt(cachedTimestamp, 10);
          
          if (age < cacheExpiry) {
            //console.log('Using cached mappings (age: ' + Math.round(age / 1000) + 's)'); //Keep
            //console.log('To fetch fresh data, run: ProductSpecsTable.clearMappingsCache()');
            return JSON.parse(cachedData);
          } else {
            console.log('Cache expired, fetching fresh mappings');
          }
        }
      } catch (e) {
        console.warn('Failed to read from cache:', e);
      }

      // Fetch fresh data
      //console.log('Fetching mappings via Cloudflare Worker...');

      const response = await fetch(this.workerUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fetchMappings: true
        })
      });

      const result = await response.json();
      //console.log('Mappings response (raw):', result);

      if (result.errors) {
        console.error('Mappings errors:', result.errors); //keep
        return {};
      }

      if (!result.data?.metaobjects?.edges) {
        return {};
      }

      // Process the metaobjects into a usable format
      const mappings = {
        byMetafieldKey: {},
        byMetaobjectKey: {}
      };
      let draftCount = 0;
      let activeCount = 0;

      result.data.metaobjects.edges.forEach(({ node }) => {
        const status = node.capabilities?.publishable?.status;
        
        if (status === 'DRAFT') {
          draftCount++;
          return;
        }
        
        activeCount++;
        
        const mapping = {};
        node.fields.forEach(field => {
          mapping[field.key] = field.value;
        });
        
        // Index by metafield_key (existing behaviour)
        if (mapping.metafield_key) {
          const key = mapping.metafield_key.split('.').pop();
          mappings.byMetafieldKey[key] = mapping;
        }
        
        // Index by metaobject_key (new — for dataset fields)
        if (mapping.metaobject_key) {
          mappings.byMetaobjectKey[mapping.metaobject_key] = mapping;
        }
      });

      //console.log(`Mappings summary: ${activeCount} active, ${draftCount} draft (skipped)`);
      //console.log('Active mappings loaded:', Object.keys(mappings).length);

      // Store in cache
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify(mappings));
        sessionStorage.setItem(cacheTimestampKey, Date.now().toString());
        //console.log('Cached mappings for 30 minutes');
      } catch (e) {
        console.warn('Failed to cache mappings:', e); //Keep
      }

      return mappings;
    }

    static clearMappingsCache() {
      try {
        sessionStorage.removeItem('shopify-specs-mappings');
        sessionStorage.removeItem('shopify-specs-mappings-timestamp');
        console.log('Mappings cache cleared');
      } catch (e) {
        console.warn('Failed to clear cache:', e);
      }
    }

    getAllKeys(variants) {
      const keys = new Set();
      const datasetKeys = new Set();

      variants.forEach(({ node: variant }) => {
        // Existing behaviour — specs or chars namespace metafields
        const metafields = this.config.namespace === 'specs'
          ? variant.specs?.edges
          : variant.chars?.edges;

        if (metafields) {
          metafields.forEach(({ node }) => {
            if (!node.key.endsWith('_tol')) {
              keys.add(node.key);
            }
          });
        }

        // Dataset metafields filtered by table type
        const datasetSuffix = this.config.namespace === 'specs'
          ? '_specifications'
          : '_characteristics';

        variant.datasets?.edges?.forEach(({ node: datasetMf }) => {
          if (!datasetMf.key.endsWith(datasetSuffix)) return;
          const metaobject = datasetMf.reference;
          if (!metaobject?.fields) return;

          metaobject.fields.forEach(field => {
            // Skip tolerance fields
            if (!field.key.endsWith('_tol')) {
              datasetKeys.add(`dataset:${field.key}`);
            }
          });
        });
      });

      return [...Array.from(keys), ...Array.from(datasetKeys)];
    }

    sortKeys(keys, mappings) {
      return keys.sort((a, b) => {
        let labelA, labelB;

        if (a === '_sku') {
          labelA = 'sku';
        } else if (a === '_weight') {
          labelA = 'weight';
        } else if (a.startsWith('dataset:')) {
          const id = a.replace('dataset:', '');
          labelA = (mappings.byMetaobjectKey[id]?.label || id).toLowerCase();
        } else {
          labelA = (mappings.byMetafieldKey[a]?.label || a).toLowerCase();
        }

        if (b === '_sku') {
          labelB = 'sku';
        } else if (b === '_weight') {
          labelB = 'weight';
        } else if (b.startsWith('dataset:')) {
          const id = b.replace('dataset:', '');
          labelB = (mappings.byMetaobjectKey[id]?.label || id).toLowerCase();
        } else {
          labelB = (mappings.byMetafieldKey[b]?.label || b).toLowerCase();
        }

        return labelA.localeCompare(labelB);
      });
    }

    getSelectedVariantId(variants) {
      // Try to get from FoxTheme's current variant
      if (window.FoxTheme?.currentVariant?.id) {
        const gid = `gid://shopify/ProductVariant/${window.FoxTheme.currentVariant.id}`;
        const found = variants.find(({ node }) => node.id === gid);
        if (found) return found.node.id;
      }
      
      // Try to get from URL parameter
      const urlParams = new URLSearchParams(window.location.search);
      const variantParam = urlParams.get('variant');
      
      if (variantParam) {
        const gid = `gid://shopify/ProductVariant/${variantParam}`;
        const found = variants.find(({ node }) => node.id === gid);
        if (found) return found.node.id;
      }
      
      // Try to get from theme's variant selector
      const variantIdInput = document.querySelector('input[name="id"]');
      if (variantIdInput && variantIdInput.value) {
        const gid = `gid://shopify/ProductVariant/${variantIdInput.value}`;
        const found = variants.find(({ node }) => node.id === gid);
        if (found) return found.node.id;
      }
      
      // Fallback to first variant
      return variants[0]?.node.id;
    }

    async render(product, mappings) {
      const variants = product.variants.edges;
      const allKeys = this.getAllKeys(variants);
      
      // Check if there are any specs/chars to display
      const hasSpecs = allKeys.length > 0;
      const hasSkuOrWeight = this.config.showSku || this.config.showWeight;
      
      if (!hasSpecs && !hasSkuOrWeight) {
        this.renderEmptyState();
        return;
      }
      
      // Add SKU and Weight to the keys for sorting
      const allRowKeys = [];
      if (this.config.showSku) allRowKeys.push('_sku');
      if (this.config.showWeight) allRowKeys.push('_weight');
      allRowKeys.push(...allKeys);
      
      const sortedKeys = this.sortKeys(allRowKeys, mappings);
      
      // Get the currently selected variant from the page
      this.currentVariantId = this.getSelectedVariantId(variants);
      
      // Restore show all state
      this.showAllVariants = this.getStoredState(`prodspec-showall-${this.sectionId}`);

      let html = `
        <table class="product-specs__table ${this.showAllVariants ? 'product-specs__table--show-all' : ''} w-full">
          <caption class="visually-hidden">Specifications</caption>
          <thead>
            <tr>
              <th class="text-left" scope="col" data-prodspec-variant-id-current="${this.currentVariantId}" data-column-index="0">
                ${this.config.tableName}
              </th>
              ${variants.map(({ node: variant }, index) => `
                <th class="text-left stick-top ${!this.showAllVariants && variant.id !== this.currentVariantId ? 'hidden' : ''}" 
                    scope="col" 
                    data-prodspec-variant-id="${variant.id}"
                    data-column-index="${index + 1}">
                  ${!product.hasOnlyDefaultVariant ? variant.title : ''}
                </th>
              `).join('')}
            </tr>
          </thead>
          <tbody>
            ${sortedKeys.map(key => {
              if (key === '_sku') return this.renderSkuRow(variants);
              if (key === '_weight') return this.renderWeightRow(variants);
              return this.renderSpecRow(key, variants, mappings);
            }).filter(row => row).join('')}
          </tbody>
        </table>
      `;

      // Fade out skeleton, then replace with real data
      const loadingTable = this.container.querySelector('.product-specs__table--loading');
      if (loadingTable) {
        loadingTable.style.opacity = '0';
        
        await new Promise(resolve => setTimeout(resolve, 300));
        this.container.innerHTML = html;
        
        // Setup column hover after render
        this.setupColumnHover();
        
        // Fade in real table
        const realTable = this.container.querySelector('.product-specs__table');
        if (realTable) {
          realTable.style.opacity = '0';
          await new Promise(resolve => setTimeout(resolve, 50));
          realTable.style.opacity = '1';
        }
      } else {
        // No skeleton found, just replace
        this.container.innerHTML = html;
        this.setupColumnHover();
      }
    }

    renderEmptyState() {
      const namespaceName = this.config.namespace === 'specs' ? 'specifications' : 'characteristics';
      
      this.container.innerHTML = `
        <div class="product-specs__empty-state" style="
          padding: 40px 20px;
          text-align: center;
          background: #f9fafb;
          border: 1px dashed #d1d5db;
          border-radius: 8px;
          color: #6b7280;
        ">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin: 0 auto 16px; opacity: 0.5;">
            <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
          </svg>
          <p style="margin: 0; font-size: 16px; font-weight: 500; color: #374151;">
            No ${namespaceName} available
          </p>
          <p style="margin: 8px 0 0; font-size: 14px;">
            This product doesn't have any ${namespaceName} data yet.
          </p>
        </div>
      `;
    }

    renderSkuRow(variants) {
      return `
        <tr>
          <th class="text-left stick-left" scope="row" data-column-index="0">SKU</th>
          ${variants.map(({ node: variant }, index) => `
            <td class="${!this.showAllVariants && variant.id !== this.currentVariantId ? 'hidden' : ''}" 
                data-prodspec-variant-id="${variant.id}"
                data-column-index="${index + 1}">
              ${variant.sku || '—'}
            </td>
          `).join('')}
        </tr>
      `;
    }

    renderWeightRow(variants) {
      return `
        <tr>
          <th class="text-left stick-left" scope="row" data-column-index="0">Weight</th>
          ${variants.map(({ node: variant }, index) => {
            const weight = variant.inventoryItem?.measurement?.weight;
            return `
              <td class="${!this.showAllVariants && variant.id !== this.currentVariantId ? 'hidden' : ''}" 
                  data-prodspec-variant-id="${variant.id}"
                  data-field="weight"
                  data-convert="g"
                  data-column-index="${index + 1}">
                <span class="cv">${weight ? weight.value : '—'}</span>
              </td>
            `;
          }).join('')}
        </tr>
      `;
    }

    renderSpecRow(key, variants, mappings) {
      // Resolve the correct mapping depending on key type
      let mapping;
      if (key.startsWith('dataset:')) {
        const id = key.replace('dataset:', '');
        mapping = mappings.byMetaobjectKey[id];
      } else {
        mapping = mappings.byMetafieldKey[key];
      }

      // Skip if no active mapping exists
      if (!mapping) {
        return '';
      }

      // Check if any variant has a value for this key
      const hasValues = variants.some(({ node: variant }) =>
        this.getMetafieldValue(variant, key)
      );

      if (!hasValues) return '';

      return `
        <tr>
          <th class="text-left stick-left" scope="row" data-column-index="0">
            ${mapping.label || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            ${mapping.iso ? `<br><span class="text-subtext text-sm">${mapping.iso}</span>` : ''}
          </th>
          ${variants.map(({ node: variant }, index) =>
            this.renderSpecCell(variant, key, mapping, index + 1)
          ).join('')}
        </tr>
      `;
    }

    getMetafieldValue(variant, key) {
      if (key.startsWith('dataset:')) {
        const fieldKey = key.replace('dataset:', ''); // e.g. "film"
        const datasetSuffix = this.config.namespace === 'specs'
          ? '_specifications'
          : '_characteristics';

        for (const { node: datasetMf } of variant.datasets?.edges || []) {
          if (!datasetMf.key.endsWith(datasetSuffix)) continue;
          const metaobject = datasetMf.reference;
          if (!metaobject?.fields) continue;

          const field = metaobject.fields.find(f => f.key === fieldKey);
          if (field && field.value !== null && field.value !== '') {
            return {
              value: field.value,
              type: 'single_line_text_field'
            };
          }
        }
        return null;
      }

      // Existing behaviour
      const metafields = this.config.namespace === 'specs'
        ? variant.specs?.edges
        : variant.chars?.edges;

      const metafield = metafields?.find(({ node }) => node.key === key);
      return metafield?.node;
    }

    getToleranceValue(variant, key) {
      if (key.startsWith('dataset:')) {
        const fieldKey = key.replace('dataset:', '');
        const tolKey = `${fieldKey}_tol`;
        const datasetSuffix = this.config.namespace === 'specs'
          ? '_specifications'
          : '_characteristics';

        for (const { node: datasetMf } of variant.datasets?.edges || []) {
          if (!datasetMf.key.endsWith(datasetSuffix)) continue;
          const metaobject = datasetMf.reference;
          if (!metaobject?.fields) continue;

          const field = metaobject.fields.find(f => f.key === tolKey);
          if (field) return field.value;
        }
        return null;
      }

      // Existing behaviour
      const toleranceKey = `${key}_tol`;

      const specs = variant.specs?.edges.find(({ node }) => node.key === toleranceKey);
      if (specs) return specs.node.value;

      const chars = variant.chars?.edges.find(({ node }) => node.key === toleranceKey);
      if (chars) return chars.node.value;

      return null;
    }

    renderSpecCell(variant, key, mapping, columnIndex) {
      const mf = this.getMetafieldValue(variant, key);
      const isHidden = !this.showAllVariants && variant.id !== this.currentVariantId;

      if (!mf) {
        return `<td class="${isHidden ? 'hidden' : ''}" data-prodspec-variant-id="${variant.id}" data-column-index="${columnIndex}">—</td>`;
      }

      let content = '';
      const value = mf.value?.trim();
      const tolerance = this.getToleranceValue(variant, key);

      switch (mf.type) {
        case 'boolean':
          content = value === 'true' 
            ? '<svg width="20" height="20" viewBox="0 0 20 20"><path d="M8 14l-4-4 1.5-1.5L8 11l6.5-6.5L16 6z" fill="currentColor"/></svg>' 
            : '<svg width="20" height="20" viewBox="0 0 20 20"><path d="M14 6l-8 8m0-8l8 8" stroke="currentColor" stroke-width="2"/></svg>';
          break;
        
        case 'product_reference':
          if (mf.reference) {
            content = `<a href="/products/${mf.reference.handle}">${mf.reference.title}</a>`;
          }
          break;
        
        case 'variant_reference':
          if (mf.reference) {
            content = `<a href="/products/${mf.reference.product.handle}?variant=${mf.reference.id}">
              ${mf.reference.product.title} - ${mf.reference.title}
            </a>`;
          }
          break;
        
        case 'list.single_line_text_field':
          try {
            const listValues = JSON.parse(value || '[]');
            content = `
              ${mapping.prefix && mapping.prefix !== 'None' ? `<span class="text-subtext text-sm">${mapping.prefix}</span> ` : ''}
              <span class="cv">${listValues.join(', ')}${mapping.formatting && mapping.formatting !== 'None' ? mapping.formatting : ''}</span>
              ${mapping.suffix && mapping.suffix !== 'None' ? ` <span class="text-subtext text-sm">${mapping.suffix}</span>` : ''}
              ${tolerance ? ` <span class="text-subtext text-sm">(±${tolerance})</span>` : ''}
            `;
          } catch (e) {
            content = value;
          }
          break;

        default:
          content = `
            ${mapping.prefix && mapping.prefix !== 'None' ? `<span class="text-subtext text-sm">${mapping.prefix}</span> ` : ''}
            <span class="cv">${value}${mapping.formatting && mapping.formatting !== 'None' ? ` ${mapping.formatting}` : ''}</span>
            ${mapping.suffix && mapping.suffix !== 'None' ? ` <span class="text-subtext text-sm">${mapping.suffix}</span>` : ''}
            ${tolerance ? ` <span class="text-subtext text-sm">(±${tolerance})</span>` : ''}
          `;
      }

      return `
        <td class="${isHidden ? 'hidden' : ''}" 
            data-prodspec-variant-id="${variant.id}"
            data-field="${key.replace(/_/g, '').toLowerCase()}"
            data-column-index="${columnIndex}"
            ${mapping.convert && mapping.convert !== 'None' ? `data-convert="${mapping.convert}"` : ''}>
          ${content}
        </td>
      `;
    }

    setupConversionToggle() {
      const convertEl = document.querySelector(`[data-convert-switch][data-section-id="${this.sectionId}"]`);
      if (!convertEl) return;

      // Restore state
      this.useImperial = this.getStoredState(`prodspec-convert-imperial-${this.sectionId}`);
      convertEl.checked = this.useImperial;

      // Calculate conversions
      this.convertMetricsToImperial();
      this.updateConversionValues(this.useImperial);

      // Listen for changes in THIS window
      convertEl.addEventListener('change', () => {
        this.useImperial = convertEl.checked;
        this.setStoredState(`prodspec-convert-imperial-${this.sectionId}`, this.useImperial);
        this.updateConversionValues(this.useImperial);
      });

      // Listen for changes from OTHER windows/tabs
      window.addEventListener('storage', (e) => {
        if (e.key === `prodspec-convert-imperial-${this.sectionId}` && e.newValue !== null) {
          const newValue = e.newValue === 'true';
          
          if (newValue !== this.useImperial) {
            this.useImperial = newValue;
            if (convertEl) convertEl.checked = newValue;
            this.updateConversionValues(this.useImperial);
          }
        }
      });
    }

    convertMetricsToImperial() {
      const conversionMap = {
        mm: { convert: val => (val / 25.4).toFixed(2), unit: 'mm', imperialUnit: 'in' },
        g: { convert: val => (val / 28.3495).toFixed(2), unit: 'g', imperialUnit: 'oz' },
        c: { convert: val => (val * 9/5 + 32).toFixed(1), unit: '°C', imperialUnit: '°F' },
        mic: { convert: val => (val / 25.4).toFixed(1), unit: 'μm', imperialUnit: 'mil' },
        kgm3: { convert: val => (val * 0.06243).toFixed(2), unit: 'kg/m³', imperialUnit: 'lb/ft³' },
        m2h: { convert: val => (val * 10.7639).toFixed(2), unit: 'm²/h', imperialUnit: 'ft²/h' },
        km: { convert: val => (val * 0.621371).toFixed(2), unit: 'km', imperialUnit: 'mi' }
      };

      const cells = this.container.querySelectorAll('[data-field]');
      //console.log('Found cells with data-field:', cells.length);

      cells.forEach(cell => {
        const span = cell.querySelector('.cv');
        if (!span) return;

        const rawVal = parseFloat(span.textContent.trim());
        const convertKey = cell.dataset.convert;

        if (!isNaN(rawVal) && convertKey && conversionMap[convertKey]) {
          const cfg = conversionMap[convertKey];
          cell.dataset.metric = `${rawVal} ${cfg.unit}`;
          cell.dataset.imperial = `${cfg.convert(rawVal)} ${cfg.imperialUnit}`;
          //console.log(`Converted ${convertKey}: ${cell.dataset.metric} → ${cell.dataset.imperial}`);
        }
      });
    }

    updateConversionValues(useImperial) {
      //console.log('Updating conversion values, useImperial:', useImperial);
      
      this.container.querySelectorAll('[data-field]').forEach(cell => {
        const span = cell.querySelector('.cv');
        if (!span) return;

        const value = useImperial ? cell.dataset.imperial : cell.dataset.metric;
        if (value) {
          //console.log('Updating cell:', value);
          span.textContent = value;
        }
      });
    }

    setupShowAllToggle() {
      const switchEl = document.querySelector(`[data-showall-switch][data-section-id="${this.sectionId}"]`);
      if (!switchEl) return;

      // Restore state
      switchEl.checked = this.showAllVariants;

      // Listen for changes in THIS window
      switchEl.addEventListener('change', () => {
        this.showAllVariants = switchEl.checked;
        this.setStoredState(`prodspec-showall-${this.sectionId}`, this.showAllVariants);
        this.animateTable(() => {
          this.toggleVariantVisibility();
          
          // Update table class
          const table = this.container.querySelector('.product-specs__table');
          if (table) {
            table.classList.toggle('product-specs__table--show-all', this.showAllVariants);
            
            // Remove any existing hover classes
            table.querySelectorAll('.column-hover, .row-hover').forEach(el => {
              el.classList.remove('column-hover', 'row-hover');
            });
            
            // Re-setup hover (will only activate if showAllVariants is true)
            this.setupColumnHover();
          }
        });
      });

      // Listen for changes from OTHER windows/tabs
      window.addEventListener('storage', (e) => {
        if (e.key === `prodspec-showall-${this.sectionId}` && e.newValue !== null) {
          const newValue = e.newValue === 'true';
          
          // Only update if the value actually changed
          if (newValue !== this.showAllVariants) {
            this.showAllVariants = newValue;
            if (switchEl) switchEl.checked = newValue;
            
            this.animateTable(() => {
              this.toggleVariantVisibility();
              
              const table = this.container.querySelector('.product-specs__table');
              if (table) {
                table.classList.toggle('product-specs__table--show-all', this.showAllVariants);
                table.querySelectorAll('.column-hover, .row-hover').forEach(el => {
                  el.classList.remove('column-hover', 'row-hover');
                });
                this.setupColumnHover();
              }
            });
          }
        }
      });
    }

    setupColumnHover() {
      const table = this.container.querySelector('.product-specs__table');
      if (!table) return;

      // Remove existing listeners if any
      const oldTable = table.cloneNode(true);
      table.parentNode.replaceChild(oldTable, table);
      const newTable = this.container.querySelector('.product-specs__table');

      // Only add hover if show all variants is active
      if (!this.showAllVariants) return;

      // Add event listeners for column and row highlighting
      newTable.addEventListener('mouseover', (e) => {
        const cell = e.target.closest('td, th');
        if (!cell) return;

        const columnIndex = cell.dataset.columnIndex;
        const row = cell.closest('tr');
        
        // Highlight column (skip column 0)
        if (columnIndex && columnIndex !== '0') {
          newTable.querySelectorAll(`[data-column-index="${columnIndex}"]`).forEach(el => {
            el.classList.add('column-hover');
          });
        }
        
        // Highlight row
        if (row) {
          row.querySelectorAll('td, th').forEach(el => {
            el.classList.add('row-hover');
          });
        }
      });

      newTable.addEventListener('mouseout', (e) => {
        const cell = e.target.closest('td, th');
        if (!cell) return;

        const columnIndex = cell.dataset.columnIndex;
        const row = cell.closest('tr');
        
        // Remove column highlight
        if (columnIndex) {
          newTable.querySelectorAll(`[data-column-index="${columnIndex}"]`).forEach(el => {
            el.classList.remove('column-hover');
          });
        }
        
        // Remove row highlight
        if (row) {
          row.querySelectorAll('td, th').forEach(el => {
            el.classList.remove('row-hover');
          });
        }
      });
    }

    toggleVariantVisibility() {
      this.container.querySelectorAll('[data-prodspec-variant-id]').forEach(el => {
        if (this.showAllVariants) {
          el.classList.remove('hidden');
        } else {
          el.classList.toggle('hidden', el.dataset.prodspecVariantId !== this.currentVariantId);
        }
      });
    }

    animateTable(callback) {
      const tableEl = this.container.querySelector('.product-specs__table');
      if (!tableEl) return callback();

      tableEl.classList.add('fade-out');

      setTimeout(() => {
        callback();
        tableEl.classList.remove('fade-out');
        tableEl.classList.add('fade-in');
        setTimeout(() => tableEl.classList.remove('fade-in'), 300);
      }, 150);
    }

    attachEventListeners() {
      // Use FoxTheme's pubsub system
      if (window.FoxTheme?.pubsub) {
        window.FoxTheme.pubsub.subscribe("variant-change", ({ data }) => {
          const variant = data?.variant;
          if (variant?.id) {
            //console.log('Variant changed via FoxTheme:', variant.id);
            const gid = `gid://shopify/ProductVariant/${variant.id}`;
            this.switchVariant(gid);
          }
        });
      } else {
        console.warn('FoxTheme.pubsub not available - variant switching may not work');
      }
    }

    switchVariant(variantId) {
      this.currentVariantId = variantId;
      
      this.animateTable(() => {
        this.toggleVariantVisibility();
        
        // Update current variant indicator
        const currentIndicator = this.container.querySelector('[data-prodspec-variant-id-current]');
        if (currentIndicator) {
          currentIndicator.dataset.prodspecVariantIdCurrent = variantId;
        }
      });
    }

    renderError() {
      this.container.innerHTML = `
        <div class="alert alert--warning blocks-radius text-center h5">
          Unable to load specifications. Please refresh the page.
        </div>
      `;
    }
  }

  // Expose to window
  window.ProductSpecsTable = ProductSpecsTable;

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTable);
  } else {
    initTable();
  }

  function initTable() {
    const containers = document.querySelectorAll('[data-product-specs-table]');
    containers.forEach(container => {
      if (!container.dataset.initialized) {
        container.dataset.initialized = 'true';
        const productHandle = container.dataset.productHandle;
        const table = new ProductSpecsTable(container, productHandle);
        table.init();
      }
    });
  }

})();